import { ChangeDetectorRef } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { UuidGenerator } from 'app/modules/main/services/uuid-generator.service';
import { QuillEditorComponent } from 'ngx-quill';
import Quill from 'quill';
import { Subscription } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

export class ContentEditorSection {

  private changeDetector: ChangeDetectorRef;
  private quillComponent?: QuillEditorComponent;
  private quillEditor?: Quill;
  private quillModules: {
    history?: any
  };

  private _componentInitialized: boolean = false;
  private _editorInitialized: boolean = false;
  private _selected: boolean = false;

  private readonly subscriptions: Subscription[] = [];

  readonly id: string;
  readonly content: BehaviorSubject<string>;

  readonly focused = new BehaviorSubject<boolean>(false);
  readonly touched = new EventEmitter<void>();

  get componentInitialized(): boolean {
    return this._componentInitialized;
  }

  get editorInitialized(): boolean {
    return this._editorInitialized;
  }

  get selected(): boolean {
    return this._selected;
  }

  constructor(
    readonly uuidGenerator: UuidGenerator,
    readonly initialContent: string = ''
  ) {
    this.id = uuidGenerator.uuid();
    this.content = new BehaviorSubject<string>(initialContent);
  }

  initialize(
    changeDetector: ChangeDetectorRef,
    quillComponent: QuillEditorComponent,
    focus: boolean = false
  ): void {

    if (this.componentInitialized) {
      console.warn('Section already initialized');
      return;
    }

    quillComponent.writeValue(this.content.value);

    quillComponent.registerOnChange(change => this.emitChange(change));
    quillComponent.registerOnTouched(() => this.emitTouch());

    this.subscriptions.push(
      quillComponent.onBlur.subscribe(() => this.blur()),
      quillComponent.onFocus.subscribe(() => this.focus()),
    );

    quillComponent.onEditorCreated
      .subscribe((quillEditor: Quill) => this.initializeEditor(quillEditor, focus));

    this.changeDetector = changeDetector;
    this.quillComponent = quillComponent;

    this._componentInitialized = true;
  }

  destroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  disable(): void {
    this.quillComponent.setDisabledState(true);
  }

  enable(): void {
    this.quillComponent.setDisabledState(false);
  }

  blur(): void {

    if (this.quillEditor?.hasFocus()) {
      setTimeout(() => this.quillEditor?.blur());
    }

    if (this.focused.value) {
      this.focused.next(false);
    }
  }

  focus(): void {

    if (!this.quillEditor?.hasFocus()) {
      setTimeout(() => this.quillEditor?.focus());
    }

    if (!this.focused.value) {
      this.focused.next(true);
    }
  }

  deselect(): void {
    this._selected = false;
  }

  select(): void {
    this._selected = true;
  }

  canUndo(): boolean {

    return this.editorInitialized
      && !!this.quillModules.history.stack.undo.length;
  }

  canRedo(): boolean {

    return this.editorInitialized
      && !!this.quillModules.history.stack.redo.length;
  }

  undo(): void {

    if (this.canUndo) {
      this.quillModules.history.undo();
    }
  }

  redo(): void {

    if (this.canRedo()) {
      this.quillModules.history.redo();
    }
  }

  private initializeEditor(
    quillEditor: Quill,
    focus: boolean
  ): void {

    this.quillEditor = quillEditor;
    this.quillModules = {
      history: quillEditor.getModule('history'),
    };

    if (focus) {
      this.focus();
    }

    this._editorInitialized = true;

    this.changeDetector.detectChanges();
  }

  private emitChange(
    change: string | null
  ): void {

    if (this.content.value !== change) {
      this.content.next(change || '');
    }
  }

  private emitTouch(): void {
    this.touched.emit();
  }

}
